/* ============================================
   Native Possum — np-mobile-menu.js
   Mobile/tablet accordion menu: hamburger open/close (all .mobile-menu-toggle),
   accordion groups (data-accordion-group), swipe to close, and the currency
   drawer. Focus, inert, Escape, aria-expanded, scroll lock and "only one of
   search / menu / bag" are npLayer's (menu and currency drawer stack).
   Depends on: np-utilities.js (npLayer, npSwitchCurrency).
   Gotcha: selectCurrency sets sessionStorage 'np-reopen-bag' so np-bag-drawer
   reopens the bag after the currency reload.
   ============================================ */

(function() {
  'use strict';

  // ============================================
  // ELEMENTS
  // ============================================
  var menuToggles = document.querySelectorAll('.mobile-menu-toggle');
  var menu = document.getElementById('npMobileMenu');
  var menuBackdrop = document.getElementById('npMobileMenuBackdrop');
  var tabletCloseBtn = menu ? menu.querySelector('.np-mobile-menu__tablet-close') : null;
  var allAccordionTriggers = document.querySelectorAll('[data-accordion-target]');
  
  // State
  var isOpen = false;

  // ============================================
  // OPEN MENU
  // Scroll lock, Escape, focus, inert page and "only one of search / menu /
  // bag" are npLayer's (group 'header'); the header follows via 'np:layer'.
  // ============================================
  function openMenu(trigger) {
    if (!menu || isOpen) return;

    isOpen = true;

    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');

    menuToggles.forEach(function(toggle) {
      toggle.classList.add('is-menu-open');
      toggle.setAttribute('aria-label', 'Close menu');
    });

    // Phone: the page behind the menu is hidden (np-mobile.css)
    if (window.innerWidth <= 767) document.body.classList.add('np-mobile-menu-open');

    // Backdrop (works at all sizes, CSS controls visibility per breakpoint)
    if (menuBackdrop) menuBackdrop.classList.add('active');

    // Tablet (768-990): the menu covers the hamburger and has its own close
    // button, so it is a true modal. Phone: the menu sits under the header and
    // the hamburger (now an X) is the only close control, so the header stays
    // usable and the menu is not aria-modal.
    var hasOwnClose = !!(tabletCloseBtn && tabletCloseBtn.getClientRects().length);
    if (hasOwnClose) {
      menu.setAttribute('aria-modal', 'true');
    } else {
      menu.removeAttribute('aria-modal');
    }
    window.npLayer.open(menu, {
      trigger: trigger,
      keep: hasOwnClose ? [] : [document.getElementById('npHeader')],
      backdrop: menuBackdrop,
      initialFocus: '.np-mobile-menu__item, .np-mobile-menu__secondary-header',
      group: 'header',
      lockScroll: true,
      onRequestClose: closeMenu
    });
  }

  // ============================================
  // CLOSE MENU
  // ============================================
  function closeMenu() {
    if (!menu || !isOpen) return;

    isOpen = false;

    // A currency drawer stacked on the menu goes with it
    closeCurrencyDrawer();

    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');

    menuToggles.forEach(function(toggle) {
      toggle.classList.remove('is-menu-open');
      toggle.setAttribute('aria-label', 'Open menu');
    });

    closeAllAccordions();

    document.body.classList.remove('np-mobile-menu-open');
    if (menuBackdrop) menuBackdrop.classList.remove('active');

    // Focus returns to the hamburger that opened it; scroll unlocks (npLayer)
    window.npLayer.close(menu);
  }

  // ============================================
  // TOGGLE MENU
  // ============================================
  function toggleMenu(e) {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu(e && e.currentTarget);
    }
  }

  // ============================================
  // ACCORDION
  // ============================================
  // Custom smooth scroll with ease-out curve (300ms)
  function smoothScrollTo(container, targetScrollTop, duration) {
    var start = container.scrollTop;
    var distance = targetScrollTop - start;
    if (Math.abs(distance) < 2) return; // Skip if already there
    if (window.npMotion.reduced()) { container.scrollTop = targetScrollTop; return; }
    var startTime = null;
    
    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic: decelerates toward end
      var eased = 1 - Math.pow(1 - progress, 3);
      container.scrollTop = start + (distance * eased);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }
    
    requestAnimationFrame(step);
  }

  function toggleAccordion(trigger) {
    var targetId = trigger.getAttribute('data-accordion-target');
    var accordion = document.getElementById(targetId);
    if (!accordion) return;
    
    var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      // Close this accordion — collapse in place, no scroll
      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      accordion.classList.remove('is-open');
      accordion.setAttribute('aria-hidden', 'true');
    } else {
      // Close ALL other open accordions across all groups (instant, no transition)
      allAccordionTriggers.forEach(function(otherTrigger) {
        if (otherTrigger !== trigger) {
          var otherId = otherTrigger.getAttribute('data-accordion-target');
          var otherAccordion = document.getElementById(otherId);
          if (otherAccordion && otherTrigger.getAttribute('aria-expanded') === 'true') {
            otherAccordion.style.transition = 'none';
            otherTrigger.classList.remove('is-expanded');
            otherTrigger.setAttribute('aria-expanded', 'false');
            otherAccordion.classList.remove('is-open');
            otherAccordion.setAttribute('aria-hidden', 'true');
            requestAnimationFrame(function() {
              otherAccordion.style.transition = '';
            });
          }
        }
      });
      
      // Open this accordion — 200ms expand + simultaneous eased scroll
      accordion.style.transition = 'max-height 0.2s ease';
      trigger.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      accordion.classList.add('is-open');
      accordion.setAttribute('aria-hidden', 'false');
      
      // Delay scroll slightly so layout recalculates with new content height
      setTimeout(function() {
        var scrollContainer = menu.querySelector('.np-mobile-menu__content');
        if (!scrollContainer) return;
        
        var containerRect = scrollContainer.getBoundingClientRect();
        var accordionRect = accordion.getBoundingClientRect();
        var triggerRect = trigger.getBoundingClientRect();
        
        // Check if the bottom of the accordion is cut off
        var bottomVisible = accordionRect.bottom <= containerRect.bottom;
        var triggerVisible = triggerRect.top >= containerRect.top;
        
        if (bottomVisible && triggerVisible) {
          // Everything fits — don't scroll
          return;
        }
        
        // Scroll trigger to 8px from top of container
        var offset = triggerRect.top - containerRect.top;
        var scrollTarget = Math.max(0, scrollContainer.scrollTop + offset - 8);
        smoothScrollTo(scrollContainer, scrollTarget, 300);
      }, 50);
    }
  }

  function closeAllAccordions() {
    allAccordionTriggers.forEach(function(trigger) {
      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
    });
    
    document.querySelectorAll('.np-mobile-menu__accordion').forEach(function(accordion) {
      accordion.classList.remove('is-open');
      accordion.setAttribute('aria-hidden', 'true');
    });
  }

  // ============================================
  // CURRENCY DRAWER
  // ============================================
  var currencyDrawer = document.getElementById('npCurrencyDrawer');
  var currencyDrawerBackdrop = document.getElementById('npCurrencyDrawerBackdrop');
  var currencyDrawerOptions = document.querySelectorAll('.np-currency-drawer__option');
  var currencyDrawerInput = document.getElementById('npCurrencyDrawerInput');
  var currencyDrawerForm = document.getElementById('npCurrencyDrawerForm');
  
  var isCurrencyDrawerOpen = false;
  var currencyDrawerTouchStartY = 0;

  function openCurrencyDrawer(trigger) {
    if (!currencyDrawer || !currencyDrawerBackdrop || isCurrencyDrawerOpen) return;
    
    isCurrencyDrawerOpen = true;
    currencyDrawer.classList.add('is-open');
    currencyDrawer.setAttribute('aria-hidden', 'false');
    currencyDrawerBackdrop.classList.add('is-open');
    
    // Stacks over the mobile menu; backdrop stays tappable to close
    window.npLayer.open(currencyDrawer, {
      trigger: trigger,
      backdrop: currencyDrawerBackdrop,
      initialFocus: '.np-currency-drawer__option',
      onRequestClose: closeCurrencyDrawer
    });
  }

  function closeCurrencyDrawer() {
    if (!currencyDrawer || !currencyDrawerBackdrop || !isCurrencyDrawerOpen) return;
    
    isCurrencyDrawerOpen = false;
    currencyDrawer.classList.remove('is-open');
    currencyDrawer.setAttribute('aria-hidden', 'true');
    currencyDrawerBackdrop.classList.remove('is-open');
    window.npLayer.close(currencyDrawer);
  }

  function selectCurrency(countryCode) {
    if (!currencyDrawerInput || !currencyDrawerForm) return;

    // v17: If bag drawer is currently open, flag it to reopen after reload
    var bagDrawerEl = document.getElementById('bagDrawer');
    if (bagDrawerEl && bagDrawerEl.classList.contains('active')) {
      try { sessionStorage.setItem('np-reopen-bag', '1'); } catch (e) {}
    }

    currencyDrawerInput.value = countryCode;
    window.npSwitchCurrency(countryCode, currencyDrawerForm);
  }

  // Delegated: this file runs while the header is parsed, before later
  // triggers exist (the bag drawer's globe button renders after the header).
  document.addEventListener('click', function(e) {
    var trigger = e.target && e.target.closest ? e.target.closest('[data-currency-drawer-trigger]') : null;
    if (!trigger) return;
    e.preventDefault();
    openCurrencyDrawer(trigger);
  });


  currencyDrawerOptions.forEach(function(option) {
    option.addEventListener('click', function() {
      var countryCode = option.getAttribute('data-country-code');
      selectCurrency(countryCode);
    });
  });

  if (currencyDrawer) {
    currencyDrawer.addEventListener('touchstart', function(e) {
      currencyDrawerTouchStartY = e.touches[0].clientY;
    }, { passive: true });

    currencyDrawer.addEventListener('touchend', function(e) {
      var touchEndY = e.changedTouches[0].clientY;
      var swipeDistance = touchEndY - currencyDrawerTouchStartY;
      if (swipeDistance > 50 && isCurrencyDrawerOpen) {
        closeCurrencyDrawer();
      }
    }, { passive: true });
  }

  // ============================================
  // SWIPE TO CLOSE (swipe left)
  // ============================================
  var touchStartX = 0;
  var touchEndX = 0;

  function handleTouchStart(e) {
    touchStartX = e.touches[0].clientX;
  }

  function handleTouchEnd(e) {
    touchEndX = e.changedTouches[0].clientX;
    var swipeDistance = touchStartX - touchEndX;
    if (swipeDistance > 100 && isOpen) {
      closeMenu();
    }
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================
  

  // Hamburger toggle — opens AND closes (all instances)
  menuToggles.forEach(function(toggle) {
    toggle.addEventListener('click', toggleMenu);
  });

  // Tablet close button
  if (tabletCloseBtn) {
    tabletCloseBtn.addEventListener('click', closeMenu);
  }
  
  // Accordion triggers
  allAccordionTriggers.forEach(function(trigger) {
    trigger.addEventListener('click', function() {
      toggleAccordion(trigger);
    });
  });
  
  // Escape and focus containment: npLayer (np-utilities.js)
  
  // Swipe to close
  if (menu) {
    menu.addEventListener('touchstart', handleTouchStart, { passive: true });
    menu.addEventListener('touchend', handleTouchEnd, { passive: true });
  }

})();